/**
 * ITER302 · Shared premium voucher card.
 *
 * ONE component consumed by three surfaces:
 *   1. Voucher Studio live preview  (admin)
 *   2. Voucher-types table thumbnail (admin)
 *   3. Guest wallet full card + thumbnail (ITER303 will hook this up)
 *
 * All three surfaces render the same JSX — thumbnails are `transform:
 * scale()` mini versions, guaranteeing that "what you design is what
 * the guest sees".  A voucher without a `design` object gets the
 * default palette so no card ever looks broken.
 */
import React from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Utensils, Wine, Cake, Gift, Crown } from 'lucide-react';
import {
  paletteFor, VALUE_SIZES, LETTER_SPACINGS, ensureGoogleFonts,
} from './voucherDesign';
import VoucherRibbon from './VoucherRibbon';
import VoucherBgPattern from './VoucherBgPattern';

const ICON_MAP = { dining: Utensils, drinks: Wine, celebration: Cake, gift: Gift, crown: Crown };

// State watermarks — applied over the card body for used/expired.
const STATE_TREATMENT = {
  available: { opacity: 1, saturation: 1, watermark: null,     showQr: true  },
  locked:    { opacity: 1, saturation: 1, watermark: null,     showQr: false },
  used:      { opacity: 0.55, saturation: 0.15, watermark: 'USED',    showQr: false },
  expired:   { opacity: 0.55, saturation: 0.15, watermark: 'EXPIRED', showQr: false },
};

import { formatBenefitValue } from './benefitFormatter';

// ITER305-refine · Non-monetary benefits (complimentary items, BOGO)
// no longer get a ₹ prefix — that used to render "₹2" for
// "Two Complimentary Welcome Desserts" which is nonsensical.
function formatValue(voucher) {
  return formatBenefitValue(voucher, 'value');
}

function OutletMark({ outletLogo, outletInitials, palette, size = 'md' }) {
  const px = size === 'sm' ? 32 : size === 'lg' ? 52 : 40;
  if (outletLogo) {
    return (
      <img
        src={outletLogo}
        alt=""
        className="rounded-full object-cover shadow-sm"
        style={{ width: px, height: px, background: 'white', padding: 2 }}
      />
    );
  }
  return (
    <div
      className="rounded-full flex items-center justify-center font-bold shadow-sm"
      style={{
        width: px, height: px,
        background: palette.accent, color: palette.surface,
        fontSize: px * 0.34, letterSpacing: '0.06em',
      }}
    >
      {outletInitials || 'RP'}
    </div>
  );
}

/**
 * <VoucherCard>
 *
 * Props (all optional except `voucher`):
 *   • voucher          — { name, subtitle, benefit_type, benefit_value,
 *                          validity_days, code, description, design }
 *   • design           — override for voucher.design (used by the studio
 *                          live preview so changes render before saving)
 *   • outletName       — resolved outlet display name
 *   • outletTagline    — resolved tagline
 *   • outletLogo       — data URI / URL, optional
 *   • outletInitials   — 2-3 letter fallback when no logo
 *   • qrValue          — string encoded into the QR (defaults to voucher.code)
 *   • state            — 'available' | 'locked' | 'used' | 'expired'
 *   • validUntil       — Date-ish string for the footer
 *   • lockedUntil      — string used for the "Available from ..." caption
 *   • size             — 'full' | 'thumb' | 'phone'  (scale + max width)
 *   • showTerms        — bool, default true
 */
export default function VoucherCard({
  voucher,
  design: designOverride,
  outletName,
  outletTagline,
  outletLogo,
  outletInitials,
  qrValue,
  state = 'available',
  validUntil,
  lockedUntil,
  size = 'full',
  showTerms = true,
  className = '',
  // Phase 2B · reveal state — legacy card must ALSO honour it so a
  // pre-reveal render never emits a scannable QR nor a plaintext code.
  revealed = false,
  qrPayload = null,
  revealedCode = null,
}) {
  const design = designOverride || voucher?.design || {};
  const palette = paletteFor(design);
  const treatment = STATE_TREATMENT[state] || STATE_TREATMENT.available;

  const IconCmp = design.icon ? ICON_MAP[design.icon] : null;
  const displayName = design.outlet_name_override || outletName || '—';
  const displayTagline = design.outlet_tagline_override || outletTagline || '';
  // Phase 2B · plaintext voucher_code is ONLY shown after Reveal.
  // Legacy vouchers previously exposed the code inline — that behaviour
  // is retired at the presentation layer without mutating the design.
  const rawCode = voucher?.code || voucher?.voucher_code || '';
  const displayCode = revealed
    ? (revealedCode || rawCode)
    : (voucher?.voucher_code_masked || (rawCode.length >= 4 ? `${rawCode.slice(0,2)}••••${rawCode.slice(-2)}` : '••••'));
  // Phase 2B · QR value only becomes scannable after Reveal.
  const scannableQR = revealed ? (qrPayload || qrValue || '') : '';

  // ── ITER302-refine · Typography ────────────────────────────────
  const typo = design.typography || {};
  const headingFont = typo.heading_font || 'Montserrat';
  const bodyFont    = typo.body_font    || 'Inter';
  const valueSize   = (VALUE_SIZES.find(v => v.key === typo.value_size)?.px) || 38;
  const trackingObj = LETTER_SPACINGS.find(l => l.key === typo.letter_spacing) || LETTER_SPACINGS[0];
  const trackingValue = trackingObj.value;
  // Lazy-load Google Fonts referenced by the current card.  Idempotent.
  React.useEffect(() => {
    ensureGoogleFonts([headingFont, bodyFont]);
  }, [headingFont, bodyFont]);

  const headingFF = `"${headingFont}", Georgia, serif`;
  const bodyFF    = `"${bodyFont}", -apple-system, "Segoe UI", sans-serif`;

  // ── ITER302-refine · Image layer sizing ────────────────────────
  const imgLayer = design.image || null;
  // ── ITER302-refine-v2 · Vertical content distribution ──────────
  // Default is 'centered' (offer sits at the visual centre); legacy
  // 'top' pins content to the top of the card.
  const alignment = design.content_alignment || 'centered';

  // ── ITER302-refine-v2 · Image / content collision protection ────
  // When ANY positioned image is present we protect text with a
  // subtle backdrop shadow so it stays readable over the image.
  const contentShadow = imgLayer?.url
    ? '0 1px 6px rgba(0,0,0,0.55), 0 0 2px rgba(0,0,0,0.35)'
    : 'none';
  // Cap background-position images to ≤ 20 % opacity so text never
  // becomes unreadable no matter what the admin set.
  const bgImgOpacity = imgLayer?.position === 'background'
    ? Math.min(imgLayer?.opacity ?? 0.15, 0.20)
    : (imgLayer?.opacity ?? 1);

  const wrapperWidth =
    size === 'thumb'  ? 'w-full max-w-[132px]'
  : size === 'phone'  ? 'w-full max-w-[340px]'
  :                     'w-full max-w-[400px]';

  const bg = design.background_image
    ? `url(${design.background_image}) center/cover`
    : palette.background;

  // ── ITER302-refine · Card edge glow.  Uses the accent colour as an
  // outer soft shadow + a 1 px inset border so the card visibly
  // "lifts" off any background (light or dark).
  const rgba = (hex, a) => {
    const h = (hex || '#000').replace('#', '');
    const n = h.length === 3
      ? h.split('').map(c => c + c).join('')
      : h.padEnd(6, '0').slice(0, 6);
    const r = parseInt(n.slice(0, 2), 16);
    const g = parseInt(n.slice(2, 4), 16);
    const b = parseInt(n.slice(4, 6), 16);
    return `rgba(${r},${g},${b},${a})`;
  };
  const edgeGlow = `0 0 22px ${rgba(palette.accent, 0.18)},`
                 + `0 4px 18px rgba(0,0,0,0.22),`
                 + `inset 0 0 0 1px ${rgba(palette.accent, 0.32)}`;

  // Loyalty-P2 · Premium visual layer options (all backward compat).
  const cornerStyle    = design.corner_style    || 'none';       // 'notched' | 'rounded' | 'bracket'
  const accentStroke   = design.accent_stroke   || 'none';       // 'thin' | 'double' | 'dashed'
  const shimmerEnabled = design.shimmer === true;                 // opt-in shimmer sheen
  const chipLabel      = design.chip_label && design.chip_label !== 'none'
                          ? design.chip_label : null;

  const strokeInset = {
    none:   null,
    thin:   `inset 0 0 0 1px ${rgba(palette.accent, 0.55)}`,
    double: `inset 0 0 0 1px ${rgba(palette.accent, 0.7)}, inset 0 0 0 5px ${rgba(palette.accent, 0.0)}, inset 0 0 0 6px ${rgba(palette.accent, 0.35)}`,
    dashed: null,   // rendered via a border style since box-shadow can't dash
  }[accentStroke];

  return (
    <div
      className={`relative rounded-2xl overflow-hidden ${wrapperWidth} ${className}`}
      style={{
        background: bg,
        color: palette.text,
        aspectRatio: '5 / 7',
        filter: `saturate(${treatment.saturation})`,
        opacity: treatment.opacity,
        boxShadow: strokeInset ? `${edgeGlow}, ${strokeInset}` : edgeGlow,
        fontFamily: bodyFF,
        containerType: 'inline-size',   // enables `cqw` font sizing above
        ...(accentStroke === 'dashed'
          ? { outline: `1px dashed ${rgba(palette.accent, 0.55)}`,
              outlineOffset: '-6px' }
          : {}),
      }}
      data-testid="voucher-card"
      data-voucher-state={state}
    >
      {/* Background pattern layer — sits behind content, above gradient */}
      <VoucherBgPattern pattern={design.bg_pattern || 'none'} color={palette.accent} />

      {/* Loyalty-P2 · Shimmer sheen — diagonal light sweep (CSS-only) */}
      {shimmerEnabled && (
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            background: `linear-gradient(120deg, transparent 20%, ${rgba(palette.accent, 0.18)} 45%, ${rgba('#ffffff', 0.28)} 50%, ${rgba(palette.accent, 0.18)} 55%, transparent 80%)`,
            mixBlendMode: 'overlay',
            animation: 'voucherShimmer 6s ease-in-out infinite',
          }}
        />
      )}
      {shimmerEnabled && (
        <style>{`
          @keyframes voucherShimmer {
            0%   { transform: translateX(-30%); opacity: 0; }
            30%  { opacity: 1; }
            60%  { opacity: 1; }
            100% { transform: translateX(30%); opacity: 0; }
          }
        `}</style>
      )}

      {/* Loyalty-P2 · Corner decorations */}
      {cornerStyle === 'notched' && (
        <>
          <span className="absolute top-1/2 -translate-y-1/2 -left-2 w-4 h-4 rounded-full"
                style={{ background: '#0B1220' }} />
          <span className="absolute top-1/2 -translate-y-1/2 -right-2 w-4 h-4 rounded-full"
                style={{ background: '#0B1220' }} />
        </>
      )}
      {cornerStyle === 'bracket' && (
        <>
          {['top-2 left-2', 'top-2 right-2', 'bottom-2 left-2', 'bottom-2 right-2'].map((pos, i) => (
            <span key={i}
                  className={`absolute ${pos} w-5 h-5 pointer-events-none`}
                  style={{
                    borderTop:    pos.includes('top')    ? `2px solid ${palette.accent}` : 'none',
                    borderBottom: pos.includes('bottom') ? `2px solid ${palette.accent}` : 'none',
                    borderLeft:   pos.includes('left')   ? `2px solid ${palette.accent}` : 'none',
                    borderRight:  pos.includes('right')  ? `2px solid ${palette.accent}` : 'none',
                    borderTopLeftRadius:     pos === 'top-2 left-2'     ? 6 : 0,
                    borderTopRightRadius:    pos === 'top-2 right-2'    ? 6 : 0,
                    borderBottomLeftRadius:  pos === 'bottom-2 left-2'  ? 6 : 0,
                    borderBottomRightRadius: pos === 'bottom-2 right-2' ? 6 : 0,
                  }} />
          ))}
        </>
      )}

      {/* Loyalty-P2 · Chip label (premium/VIP/special pill) */}
      {chipLabel && (
        <div
          className="absolute z-30 uppercase font-bold rounded-full"
          data-testid="voucher-chip-label"
          style={{
            top: 12, right: 12,
            padding: '4px 10px',
            fontSize: 8.5,
            letterSpacing: '0.18em',
            color: palette.surface,
            background: `linear-gradient(135deg, ${palette.accent} 0%, ${rgba(palette.accent, 0.75)} 100%)`,
            boxShadow: `0 2px 6px ${rgba(palette.accent, 0.35)}, inset 0 0 0 1px ${rgba('#ffffff', 0.35)}`,
          }}
        >
          {chipLabel}
        </div>
      )}

      {/* Background image layer (full-bleed / low-opacity watermark) */}
      {imgLayer?.url && imgLayer.position === 'background' && (
        <img
          src={imgLayer.url} alt=""
          className="absolute inset-0 w-full h-full object-cover pointer-events-none"
          style={{ opacity: bgImgOpacity }}
        />
      )}

      {/* Ribbon overlay (badge text piped into diagonal/top_banner/bottom_sash) */}
      <VoucherRibbon variant={design.ribbon_variant || 'corner_bow'}
                     color={palette.accent}
                     badgeText={design.badge && design.badge !== 'None' ? design.badge : ''} />

      {/* Badge — top-left, cleared from the branding area.
          ITER302-refine-v3 · The badge is now a self-contained pill
          in the top-left with enough top+left margin to never
          collide with the (centered) branding block below. */}
      {design.badge && design.badge !== 'None' && (
        <div
          className="absolute z-20 font-bold uppercase rounded-full shadow-sm"
          style={{
            top: 12, left: 12,
            background: palette.accent,
            color: palette.surface,
            fontSize: 8.5,
            letterSpacing: '0.16em',
            padding: '3px 8px',
          }}
        >
          {design.badge}
        </div>
      )}

      <div
        className="relative h-full flex flex-col"
        style={{
          justifyContent: alignment === 'centered' ? 'space-between' : 'flex-start',
          textShadow: contentShadow,
          padding: '20px 16px 16px 16px',    // ITER302-refine-v3 · 20 px top breathing
        }}
      >
        {/* ─── TOP SECTION · outlet identity (CENTERED, no truncate) ─── */}
        <div className="flex flex-col items-center text-center gap-1.5 mt-1">
          <OutletMark outletLogo={outletLogo}
                      outletInitials={outletInitials}
                      palette={palette}
                      size={size === 'thumb' ? 'sm' : 'md'} />
          {/* Outlet name — wraps to 2 lines, auto-scales down via CSS clamp
              on very long names. No `truncate` — a premium card never
              shows "…" on the brand. */}
          <div
            className="font-bold uppercase leading-tight w-full px-1"
            style={{
              color: palette.text,
              fontFamily: headingFF,
              letterSpacing: trackingValue,
              fontSize: 'clamp(9px, 2.6cqw, 12px)',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              wordBreak: 'break-word',
              hyphens: 'auto',
            }}
          >
            {displayName}
          </div>
          {displayTagline && (
            <div
              className="uppercase leading-tight w-full"
              style={{
                color: palette.textMuted,
                fontFamily: headingFF,
                letterSpacing: trackingValue,
                fontSize: 'clamp(7px, 2cqw, 10px)',
                display: '-webkit-box',
                WebkitLineClamp: 1,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {displayTagline}
            </div>
          )}
        </div>

        {/* ─── CENTER SECTION · the offer ─── */}
        <div className="text-center px-1">
          <div className="flex items-center gap-2 mb-1 justify-center">
            {IconCmp && (
              <IconCmp
                className="shrink-0"
                style={{ color: palette.accent, width: valueSize * 0.55, height: valueSize * 0.55 }}
                strokeWidth={1.8}
              />
            )}
            <div
              className="leading-none font-bold tabular-nums"
              style={{
                color: palette.accent,
                fontFamily: headingFF,
                fontSize: valueSize,
              }}
            >
              {formatValue(voucher)}
            </div>
          </div>
          <div className="text-[13px] font-semibold leading-tight uppercase"
               style={{ color: palette.text, fontFamily: headingFF, letterSpacing: '0.08em' }}>
            {voucher?.name || 'Voucher'}
          </div>
          {(voucher?.subtitle || voucher?.description) && (
            <div className="text-[10px] leading-snug mt-1 max-w-full px-2 line-clamp-2"
                 style={{ color: palette.textMuted, fontFamily: bodyFF }}>
              {voucher.subtitle || voucher.description}
            </div>
          )}
        </div>

        {/* ─── BOTTOM SECTION · redemption + terms ─── */}
        <div className="flex flex-col items-center">
          {/* Phase 2B · pre-Reveal legacy cards do NOT emit a scannable
              QR nor the plaintext code. If the card is not yet revealed
              we render a tap-to-reveal placeholder instead. */}
          {treatment.showQr && scannableQR ? (
            <div className="flex flex-col items-center gap-1.5">
              <div className="p-0">
                <QRCodeSVG
                  value={scannableQR}
                  size={size === 'thumb' ? 44 : 60}
                  bgColor="transparent"
                  fgColor={palette.accent}
                  level="M"
                  includeMargin={false}
                />
              </div>
              <div className="text-center">
                <div className="font-mono text-[10.5px] font-semibold tracking-wider break-all"
                     style={{ color: palette.text }}>
                  {displayCode}
                </div>
                {validUntil && (
                  <div className="text-[9px] mt-0.5"
                       style={{ color: palette.textMuted }}>
                    Valid until {validUntil}
                  </div>
                )}
              </div>
            </div>
          ) : state === 'locked' ? (
            <div className="text-[10px] px-2 py-1 rounded-md text-center"
                 style={{ background: 'rgba(255,255,255,0.14)', color: palette.text }}>
              Available from {lockedUntil || 'later'}
            </div>
          ) : null}

          {showTerms && (
            <div className="text-[8.5px] uppercase mt-2 opacity-70 text-center"
                 style={{ color: palette.textMuted, letterSpacing: trackingValue }}>
              View Terms & Conditions
            </div>
          )}
        </div>
      </div>

      {/* Positioned image layer — rendered ABOVE content but below watermark */}
      {imgLayer?.url && imgLayer.position !== 'background' && (
        <PositionedImage layer={imgLayer} />
      )}

      {/* State watermark (USED / EXPIRED) */}
      {treatment.watermark && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div
            className="text-4xl font-black tracking-[0.3em] px-6 py-2 rounded-md border-4"
            style={{
              color: '#B91C1C',
              borderColor: '#B91C1C',
              background: 'rgba(255,255,255,0.85)',
              transform: 'rotate(-14deg)',
            }}
          >
            {treatment.watermark}
          </div>
        </div>
      )}
    </div>
  );
}

// ── ITER302-refine · Positioned image layer ────────────────────────
// Renders a user-uploaded image at one of six anchors + size + opacity.
function PositionedImage({ layer }) {
  const { url, position = 'bottom_right', size = 'medium', opacity = 1 } = layer;
  const px = size === 'small' ? 60 : size === 'large' ? 120 : size === 'full_width' ? null : 90;
  const anchorClass = {
    top_left:      'top-3 left-3',
    top_right:     'top-3 right-3',
    bottom_left:   'bottom-3 left-3',
    bottom_right:  'bottom-3 right-3',
    bottom_center: 'bottom-3 left-1/2 -translate-x-1/2',
    middle_center: 'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2',
  }[position] || 'bottom-3 right-3';
  return (
    <img
      src={url} alt=""
      className={`absolute ${anchorClass} pointer-events-none object-cover rounded-md shadow-md`}
      style={{
        width: px ? `${px}px` : '90%',
        height: px ? `${px}px` : 'auto',
        opacity,
      }}
    />
  );
}
